import { LoadingSpinner } from "components/ui/loading-spinner";
import MarkdownPreview from "components/ui/MarkdownPreview";
import { AlertCircle, CheckCircle, Circle } from "lucide-react";
import React, { useState } from "react";
import { cn } from "src/common/utils";

import { DagNodeButtonProps } from "./types";

export interface InlineDagNodeButtonProps<T> extends DagNodeButtonProps<T> {
  inline?: boolean;
  getNodeResult?: (node: T) => string | undefined;
  showViewDetails?: boolean;
}

/**
 * DagNodeButton component for DAG visualization
 * This is a customizable button for rendering DAG nodes with status-based styling
 */
const DagNodeButton = React.forwardRef<
  HTMLButtonElement,
  InlineDagNodeButtonProps<any>
>(function DagNodeButton({
  node,
  getNodeTitle,
  getNodeStatus,
  getNodeId,
  getNodeResult = () => undefined,
  isActive,
  isDragging,
  isClickable,
  onClick,
  inline = false,
  showViewDetails = true
}, ref) {
  const [isHovering, setIsHovering] = useState(false);
  const status = getNodeStatus(node);
  const nodeId = getNodeId(node);
  
  // Determine if the node is clickable (for visual cues)
  const clickableNode = isClickable !== false;
  
  // Determine styling based on status
  const getStatusStyles = () => {
    switch (status) {
      case "SUCCESS":
        return {
          bg: "bg-slate-850 hover:bg-slate-800/90",
          border: isActive || isHovering 
            ? "border-blue-500" 
            : inline ? "border-blue-800/50" : "border-green-600/50",
          textColor: inline
            ? (isActive || isHovering ? "text-primary" : "text-primary")
            : (isActive || isHovering ? "text-green-300" : "text-green-400"),
          cursor: clickableNode ? "cursor-pointer" : "cursor-not-allowed"
        };
      case "ERROR":
        return {
          bg: "bg-slate-850 hover:bg-slate-800",
          border: isActive || isHovering ? "border-blue-500" : "border-red-800/50",
          textColor: isActive || isHovering ? "text-red-300" : "text-red-400",
          cursor: "cursor-not-allowed opacity-80"
        };
      case "IN_PROGRESS":
        return {
          bg: "bg-slate-850 hover:bg-slate-800",
          border: isActive || isHovering ? "border-blue-500" : "border-blue-800/50",
          textColor: isActive || isHovering ? "text-primary" : "text-primary",
          cursor: "cursor-not-allowed opacity-80"
        };
      default: // PENDING
        return {
          bg: "bg-slate-900 hover:bg-slate-800",
          border: isActive || isHovering ? "border-blue-500" : "border-slate-700",
          textColor: isActive || isHovering ? "text-primary" : "text-slate-400",
          cursor: "cursor-not-allowed opacity-80"
        };
    }
  };
  
  const statusStyles = getStatusStyles();
  
  const handleClick = (e: React.MouseEvent) => {
    // Skip click if node is not clickable
    if (isClickable === false) {
      e.preventDefault();
      return;
    }
    
    onClick?.();
  };
  
  // Get the node result text
  const nodeResult = getNodeResult?.(node) || "";

  return (
    <button
      ref={ref as React.RefObject<HTMLButtonElement>}
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className={cn(
        "flex flex-col items-start gap-2 text-slate-300 rounded border relative z-10 transition-all duration-200",
        // Adjust padding and display for inline mode
        inline ? "inline-flex px-1 py-0.5 m-0 align-bottom" : "px-4 py-3",
        statusStyles.bg,
        statusStyles.cursor,
        // Make clickable nodes stand out with subtle hover effects
        clickableNode &&
          !inline &&
          "hover:shadow-lg hover:border-blue-400/70 hover:bg-slate-800",
        isActive
          ? "border-blue-500 shadow-[0_0_0_1px_rgba(59,130,246,0.3)] ring-1 ring-blue-500 bg-slate-800"
          : isHovering && isClickable !== false
            ? "border-slate-500 shadow-md"
            : statusStyles.border,
        isDragging && "opacity-50",
      )}
      data-node-id={nodeId}
      disabled={isClickable === false}
    >
      {inline ? (
        <span
          className={cn(
            "text-xs transition-colors duration-200",
            statusStyles.textColor,
          )}
        >
          [{nodeId}]
        </span>
      ) : (
        <>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              {status === "SUCCESS" ? (
                <CheckCircle
                  className={cn(
                    "h-5 w-5 flex-shrink-0 transition-colors duration-200",
                    statusStyles.textColor,
                  )}
                />
              ) : status === "ERROR" ? (
                <AlertCircle
                  className={cn(
                    "h-5 w-5 flex-shrink-0 transition-colors duration-200",
                    statusStyles.textColor,
                  )}
                />
              ) : status === "IN_PROGRESS" ? (
                <LoadingSpinner
                  className={cn(
                    "h-5 w-5 flex-shrink-0",
                    statusStyles.textColor,
                  )}
                />
              ) : (
                <Circle
                  className={cn(
                    "h-5 w-5 flex-shrink-0 transition-colors duration-200",
                    statusStyles.textColor,
                  )}
                />
              )}
              <div className="font-semibold text-sm">
                [{nodeId}] {getNodeTitle(node)}
              </div>
            </div>

            {/* Add a visual indicator for clickable nodes */}
            {clickableNode && showViewDetails && (
              <div className="text-primary text-xs font-medium border border-primary/30 rounded-full px-2 py-0.5 bg-primary/10 ml-1">
                View Details
              </div>
            )}
          </div>

          {/* Show result preview for completed nodes */}
          {status === "SUCCESS" && nodeResult && (
            <div className="w-full mt-1 pt-2 border-t border-slate-700/50">
              <div className="text-xs text-slate-300 line-clamp-3 text-left rounded bg-slate-800/50 p-2 overflow-hidden">
                <MarkdownPreview maxLength={300}>
                  {nodeResult}
                </MarkdownPreview>
              </div>
            </div>
          )}
        </>
      )}
    </button>
  );
});

export default DagNodeButton;